import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Defrost } from 'src/app/model/defrost';
import { SensorData } from 'src/app/model/sensorData';
import { DefrostService } from 'src/app/services/defrost.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { ReasonDialogComponent } from '../../shared/reason-dialog/reason-dialog.component';
import { ExecutionEditDefrostEditComponent } from './execution-edit-defrost-edit.component';

export interface DialogDataDefrostList {
  idEssay: number;
  data: SensorData[];
  idEssayType: number;
  /** Tiempo de ensayo declarado ("dias;horas;minutos"), para la franja por defecto de un evento nuevo. */
  rehearsalTime?: string;
}

@Component({
    selector: 'app-execution-edit-defrost-list',
    templateUrl: './execution-edit-defrost-list.component.html',
    standalone: false,
})
export class ExecutionEditDefrostListComponent implements OnInit, OnDestroy {

  displayedColsUsers = ['index', 'startDate', 'endDate', 'type', 'edit', 'delete'];

  defrosts: Defrost[];

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ExecutionEditDefrostListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataDefrostList,
    private defrostService: DefrostService,
    private translate: TranslateService,
    public dialog: MatDialog,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService) { }

  ngOnInit(): void {
    this.reloadTable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadTable(): void {
    this.spinnerService.show();
    this.defrostService.findAllFromEssay(this.data.idEssay).pipe(takeUntil(this.destroy$)).subscribe((item: Defrost[]) => {
      this.defrosts = item;
      this.spinnerService.hide();
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  newDefrost(): void {
    this.openDialogEdit(new Defrost());
  }

  editRow(id: number): void {
    this.defrostService.findOne(id).pipe(takeUntil(this.destroy$)).subscribe((item: Defrost) => this.openDialogEdit(item));
  }

  deleteRow(id: number): void {
    const message = this.translate.instant('executionEdit.events.form.save.confirm') as string;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result === true) {
        this.requestReason(reason => {
          this.spinnerService.show();
          this.defrostService.delete(id, reason).pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.reloadTable();
            this.spinnerService.hide();
          }, err => {
            const message = typeof err?.error === 'string' && err.error.trim() !== ''
              ? err.error : this.translate.instant('executionEdit.events.form.save.error.generic') as string;
            this.snackBarService.sendError(message);
            this.spinnerService.hide();
          });
        });
      }
    });

  }

  private openDialogEdit(defrost: Defrost) {
    const dialogRef = this.dialog.open(ExecutionEditDefrostEditComponent, {
      minWidth: '50%',
      maxHeight: '95vh',
      data: {
        defrost,
        data: this.data.data,
        idEssayType: this.data.idEssayType,
        rehearsalTime: this.data.rehearsalTime
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: Defrost) => {
      if (result != null) {
        result.idEssay = this.data.idEssay;
        if (result.id != null) {
          this.requestReason(reason => {
            result.reasonChange = reason;
            this.saveDefrost(result);
          });
        } else {
          // El alta deja asiento automáticamente; solo editar/borrar pide un motivo.
          this.saveDefrost(result);
        }
      }
    });
  }

  private requestReason(callback: (reason: string) => void): void {
    const dialogRef = this.dialog.open(ReasonDialogComponent, {
      minWidth: '40%',
      maxHeight: '95vh',
      data: { title: 'common.reasonChange', reason: '' }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((reason: string) => {
      if (reason == null) {
        return;
      }
      if (typeof reason !== 'string' || reason.trim() === '') {
        this.snackBarService.sendError(this.translate.instant('common.reasonChangeError') as string);
        return;
      }
      callback(reason.trim());
    });
  }

  private saveDefrost(defrost: Defrost): void {
    this.spinnerService.show();
    this.defrostService.save(defrost).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.snackBarService.sendSuccess(this.translate.instant('executionEdit.events.form.save.ok') as string);
      this.spinnerService.hide();
      this.reloadTable();
    }, () => {
      this.snackBarService.sendError(this.translate.instant('executionEdit.events.form.save.error.generic') as string);
      this.spinnerService.hide();
      this.reloadTable();
    });
  }

}
